import { CalendarIcon } from "@radix-ui/react-icons";
import {
  endOfMonth,
  endOfYear,
  format,
  previousSunday,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";

import { CalendarDates, CalendarPreset } from "@/custom-types";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { DateRange, DayClickEventHandler } from "react-day-picker";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export default function CalendarDateRangePicker({
  date,
  setDate,
  className,
  startFrom,
  presetVariant = "default",
}: {
  date: DateRange | undefined;
  setDate: (selectedDate: CalendarDates | undefined) => void;
  className?: string;
  startFrom?: Date;
  presetVariant?: "default" | "longRange" | "weeks";
}) {
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [activePreset, setActivePreset] = useState<CalendarPreset | null>(null);
  const [tempSelection, setTempSelection] = useState<{ from?: Date; to?: Date }>({});

  const handleDayClick: DayClickEventHandler = day => {
    // Reset preset when user clicks manually
    setActivePreset(null);

    // Case 1: no start date yet → set start
    if (!tempSelection.from) {
      setTempSelection({ from: day });
      return;
    }

    // Case 2: start exists, but no end date → complete the range
    if (!tempSelection.to) {
      // Ensure "from" is always before "to"
      const from = day < tempSelection.from ? day : tempSelection.from;
      const to = day < tempSelection.from ? tempSelection.from : day;

      setTempSelection({ from, to });
      setDate({ from, to });
      setIsCalendarOpen(false);
      return;
    }

    // Case 3: both dates exist → start a new selection
    setTempSelection({ from: day });
  };

  const selectLast7Days = () => {
    const today = new Date();
    const from = subDays(today, 7);
    setDate({ from, to: today });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("last7");
  };

  const selectLast14Days = () => {
    const today = new Date();
    const from = subDays(today, 14);
    setDate({ from, to: today });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("last14");
  };

  const selectLastMonth = () => {
    const today = new Date();
    const firstDayLastMonth = startOfMonth(subMonths(today, 1));
    const lastDayLastMonth = endOfMonth(subMonths(today, 1));
    setDate({ from: firstDayLastMonth, to: lastDayLastMonth });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("lastMonth");
  };

  const selectThisMonth = () => {
    const today = new Date();
    const firstDayThisMonth = startOfMonth(today);
    const lastDayThisMonth = endOfMonth(today);
    setDate({ from: firstDayThisMonth, to: lastDayThisMonth });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("thisMonth");
  };

  const selectLast30Days = () => {
    const today = new Date();
    const from = subDays(today, 30);
    setDate({ from, to: today });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("last30");
  };

  const selectLast3Months = () => {
    const today = new Date();
    const firstDayLast3Months = startOfMonth(subMonths(today, 3));
    const lastDayLast3Months = endOfMonth(subMonths(today, 1));
    setDate({ from: firstDayLast3Months, to: lastDayLast3Months });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("last3Months");
  };

  const selectLast6Months = () => {
    const today = new Date();
    const firstDayLast6Months = startOfMonth(subMonths(today, 6));
    const lastDayLast6Months = endOfMonth(subMonths(today, 1));
    setDate({ from: firstDayLast6Months, to: lastDayLast6Months });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("last6Months");
  };

  const selectLastYear = () => {
    const today = new Date();
    const firstDayLastYear = startOfYear(subYears(today, 1));
    const lastDayLastYear = endOfYear(subYears(today, 1));
    setDate({ from: firstDayLastYear, to: lastDayLastYear });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset("lastYear");
  };

  const lastFullSunday = (() => {
    const today = new Date();
    // If today is Sunday, use today; otherwise use the most recent Sunday
    return today.getDay() === 0 ? today : previousSunday(today);
  })();

  const selectLastNWeeks = (n: number, preset: CalendarPreset) => {
    const to = lastFullSunday;
    const from = subWeeks(to, n);
    // Start on Monday (day after the Sunday n weeks ago)
    from.setDate(from.getDate() + 1);
    setDate({ from, to });
    setTempSelection({});
    setIsCalendarOpen(false);
    setActivePreset(preset);
  };

  const handlePopoverOpenChange = (open: boolean) => {
    setIsCalendarOpen(open);
    if (open) setTempSelection({});
  };

  const displayRange = tempSelection.from ? tempSelection : date;
  const showLongRangePresets = presetVariant === "longRange";
  const showWeeksPresets = presetVariant === "weeks";

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={isCalendarOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn("w-[260px] justify-start text-left font-normal", !date && "text-muted-foreground")}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} - {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <div className="flex">
            <Calendar
              fromDate={startFrom}
              mode="range"
              defaultMonth={date?.from}
              selected={
                displayRange?.from
                  ? {
                      from: displayRange.from,
                      to: displayRange.to || displayRange.from,
                    }
                  : undefined
              }
              onDayClick={handleDayClick}
              numberOfMonths={2}
            />
            <div className="border-l border-border">
              <div className="p-3 flex flex-col gap-2">
                <div className="text-sm font-medium mb-1">Presets</div>
                {showWeeksPresets ? (
                  <>
                    <Button
                      variant={activePreset === "last8Weeks" ? "default" : "outline"}
                      size="sm"
                      className="text-xs justify-start"
                      onClick={() => selectLastNWeeks(8, "last8Weeks")}
                    >
                      Last 8 weeks
                    </Button>
                    <Button
                      variant={activePreset === "last16Weeks" ? "default" : "outline"}
                      size="sm"
                      className="text-xs justify-start"
                      onClick={() => selectLastNWeeks(16, "last16Weeks")}
                    >
                      Last 16 weeks
                    </Button>
                    <Button
                      variant={activePreset === "last32Weeks" ? "default" : "outline"}
                      size="sm"
                      className="text-xs justify-start"
                      onClick={() => selectLastNWeeks(32, "last32Weeks")}
                    >
                      Last 32 weeks
                    </Button>
                    <Button
                      variant={activePreset === "last64Weeks" ? "default" : "outline"}
                      size="sm"
                      className="text-xs justify-start"
                      onClick={() => selectLastNWeeks(64, "last64Weeks")}
                    >
                      Last 64 weeks
                    </Button>
                  </>
                ) : (
                  <>
                    {showLongRangePresets && (
                      <Button
                        variant={activePreset === "last30" ? "default" : "outline"}
                        size="sm"
                        className="text-xs justify-start"
                        onClick={selectLast30Days}
                      >
                        Last 30 days
                      </Button>
                    )}
                    <Button
                      variant={activePreset === "lastMonth" ? "default" : "outline"}
                      size="sm"
                      className="text-xs justify-start"
                      onClick={selectLastMonth}
                    >
                      Last month
                    </Button>
                    {showLongRangePresets ? (
                      <>
                        <Button
                          variant={activePreset === "last3Months" ? "default" : "outline"}
                          size="sm"
                          className="text-xs justify-start"
                          onClick={selectLast3Months}
                        >
                          Last 3 months
                        </Button>
                        <Button
                          variant={activePreset === "last6Months" ? "default" : "outline"}
                          size="sm"
                          className="text-xs justify-start"
                          onClick={selectLast6Months}
                        >
                          Last 6 months
                        </Button>
                        <Button
                          variant={activePreset === "lastYear" ? "default" : "outline"}
                          size="sm"
                          className="text-xs justify-start"
                          onClick={selectLastYear}
                        >
                          Last year
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant={activePreset === "last7" ? "default" : "outline"}
                          size="sm"
                          className="text-xs justify-start"
                          onClick={selectLast7Days}
                        >
                          Last 7 days
                        </Button>
                        <Button
                          variant={activePreset === "last14" ? "default" : "outline"}
                          size="sm"
                          className="text-xs justify-start"
                          onClick={selectLast14Days}
                        >
                          Last 14 days
                        </Button>
                        <Button
                          variant={activePreset === "thisMonth" ? "default" : "outline"}
                          size="sm"
                          className="text-xs justify-start"
                          onClick={selectThisMonth}
                        >
                          This month
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
